import { createRef } from 'react'

import { ComponentMapping, Container } from '@adobe/aem-react-editable-components'

import { ReactLayoutStyled } from './ReactLayout.styled'
import { HorizontalAlignment, LayoutBackgroundTypes, ReactLayoutProps, ReactLayoutState } from './types'
import './ReactLayout.scss'
import { backgroundClassTypeMap } from '../../utils/helper-util'
import { getSpacing } from '../../utils/spacing-util'
import IsInEditorText from '../shared/IsInEditorText'

class ReactLayout extends Container<ReactLayoutProps, ReactLayoutState> {
  private stickyRef: React.RefObject<HTMLDivElement>
  private initialOffset: number = 0
  constructor(props: ReactLayoutProps) {
    super(props)
    this.stickyRef = createRef()
    this.state = {
      componentMapping: this.props.componentMapping || ComponentMapping,
      dismissed: false
    }
    this.handleDismiss = this.handleDismiss.bind(this)
    this.applyStickyPosition = this.applyStickyPosition.bind(this)
  }

  handleDismiss() {
    this.setState({ ...this.state, dismissed: true })
  }

  applyStickyPosition() {
    const stickyElement = this.stickyRef?.current
    if (stickyElement && this.initialOffset > 0 && this.props.isSticky) {
      // Component becomes sticky when scrolled past its initial position
      const shouldBeSticky = window.scrollY > this.initialOffset
      if (shouldBeSticky) {
        this.stickyRef?.current?.classList.add('react-layout--sticky')
      } else {
        this.stickyRef?.current?.classList.remove('react-layout--sticky')
      }
    }
  }

  componentDidMount(): void {
    if (this.props.isSticky) {
      // Capture the initial position of the element
      if (this.stickyRef?.current) {
        this.initialOffset = this.stickyRef.current.offsetTop
      }
      window.addEventListener('scroll', this.applyStickyPosition)
    }
  }

  componentWillUnmount(): void {
    if (this.props.isSticky) {
      window.removeEventListener('scroll', this.applyStickyPosition)
    }
  }

  displaySection(section: JSX.Element, index: number) {
    const { isInEditor, horizontalAlignment, hideInMobile } = this.props
    const bootstrapGridClass = section.props.bootstrapGridClasses
    const displayClass = hideInMobile ? (index === 1 ? 'd-none d-lg-flex' : 'd-flex') : 'd-flex'
    const horizontalAlignmentClass =
      horizontalAlignment !== HorizontalAlignment.default
        ? `align-items-${HorizontalAlignment[horizontalAlignment as keyof typeof HorizontalAlignment]} text-${
            HorizontalAlignment[horizontalAlignment as keyof typeof HorizontalAlignment]
          }`
        : ''
    return (
      <div
        key={'section-' + index}
        className={`${bootstrapGridClass} ${displayClass} flex-column ${horizontalAlignmentClass}`}
      >
        {isInEditor && <div className='editor-text'>Layout Section {index + 1}</div>}
        {section}
      </div>
    )
  }

  render() {
    const {
      isInEditor = false,
      layoutBackground,
      backgroundColor,
      reverseStacking,
      sideSpace = false,
      borderRadius = false,
      top = false,
      right = false,
      bottom = false,
      left = false,
      lob = '',
      hideMCP = false,
      isDismissable = false,
      isSticky = false
    } = this.props
    const reverseStackingClass = reverseStacking ? `flex-lg-row flex-column-reverse` : ''
    const bgColor = layoutBackground === LayoutBackgroundTypes.color ? backgroundColor : ''
    const removeLayoutSectionPadding = sideSpace === true ? 'remove-layout-section-padding' : ''
    const hasBorder = borderRadius === true ? 'bordered' : ''

    // Checks if hideMCP flag is true and that display is in dispatcher, not in author:
    const mcpClass = hideMCP && isInEditor == false ? 'd-none' : ''

    return (
      <>
        {IsInEditorText(isInEditor, 'React Layout')}
        {!this.state.dismissed && (
          <div
            data-testid='react-layout'
            className={`react-layout ${getSpacing(top, right, bottom, left)} ${lob} ${mcpClass}`}
            ref={this.stickyRef}
          >
            {isDismissable && isSticky && (
              <button onClick={this.handleDismiss} aria-label='Dismiss layout' className='react-layout__dismiss-button'>
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'>
                  <path
                    d='M9.13087 8.00004L14.4285 2.70242C14.746 2.38496 14.746 1.88893 14.4285 1.57147C14.111 1.25401 13.615 1.25401 13.2975 1.57147L7.99992 6.86909L2.7023 1.59131C2.38484 1.27385 1.88881 1.27385 1.57135 1.59131C1.25389 1.90877 1.25389 2.4048 1.57135 2.72226L6.86897 8.00004L1.59119 13.2977C1.27373 13.6151 1.27373 14.1112 1.59119 14.4286C1.74992 14.5873 1.94833 14.6667 2.14674 14.6667C2.34516 14.6667 2.54357 14.5873 2.7023 14.4286L7.99992 9.13099L13.2975 14.4286C13.4563 14.5873 13.6547 14.6667 13.8531 14.6667C14.0515 14.6667 14.2499 14.5873 14.4087 14.4286C14.7261 14.1112 14.7261 13.6151 14.4087 13.2977L9.13087 8.00004Z'
                    fill={
                      backgroundClassTypeMap[backgroundColor || ''] === 'dark'
                        ? 'var(--color-neutral-100)'
                        : 'var(--color-neutral-600)'
                    }
                  />
                </svg>
              </button>
            )}
            <ReactLayoutStyled
              {...this.props}
              className={`row ${bgColor} ${reverseStackingClass} ${removeLayoutSectionPadding} ${hasBorder} mobile-spacing ${isSticky && 'react-layout--sticky-padding'}`}
            >
              {this.childComponents.map((childComponent, index) => this.displaySection(childComponent, index))}
            </ReactLayoutStyled>
          </div>
        )}
      </>
    )
  }
}

export default ReactLayout
